import {
  ApolloClient,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
  from
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { addAttribute, logError } from './datadog';

export const ApolloClientProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const authLink = setContext((_, { headers }) => {
    return {
      headers: {
        ...headers,
        authorization: process.env.REACT_APP_GRAPHQL_AUTHORIZATION_TOKEN
      }
    };
  });

  const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    const { operationName, variables, query, getContext } = operation;
    const { headers, response } = getContext() || {};

    addAttribute('operationName', operation?.operationName || '');
    addAttribute('variables', JSON.stringify(variables));
    addAttribute('query', JSON.stringify(query));
    addAttribute('gqlQuery', query.loc?.source?.body || '');
    addAttribute('response', JSON.stringify(response));
    addAttribute('headers', JSON.stringify(headers));

    if (networkError) {
      logError(networkError, {
        operationName,
        variables,
        headers,
        type: 'networkError'
      });
    }

    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path }) => {
        const error = new Error(message);
        logError(error, {
          operationName,
          variables,
          locations,
          path,
          type: 'graphQLError'
        });
      });
    }
  });

  const client = new ApolloClient({
    link: from([
      errorLink,
      new RetryLink({
        attempts: {
          max: 3
        }
      }),
      authLink,
      new HttpLink({
        uri: process.env.REACT_APP_GRAPHQL_ENDPOINT
      })
    ]),
    cache: new InMemoryCache()
  });

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
